import { fabric } from 'fabric';
import type { FabricEditor } from '../core/fabric-editor';
import type { EditorPlugin } from '../interfaces/editor-plugin';
import { FabricPluginName } from './plugin-name';

/**
 * The side the fill runs toward: the gradient holds the start stop at the opposite
 * edge and reaches the end stop at the named side. `right` keeps the left edge at
 * the start color and transitions rightward, `down` keeps the top, etc.
 */
export type FillGradientDirection = 'right' | 'left' | 'up' | 'down';

/**
 * Two-stop linear gradient fill, stored on `data.fillGradient`. The visible `fill`
 * gradient is derived from it (see `buildFillGradient`), so `data` stays the single
 * source of truth — both round-trip through `toJSON`/`loadFromJSON` and undo/redo
 * (Fabric serializes a gradient `fill` natively; `data` is in the history include
 * list).
 *
 * A vignette ("fade-out") is not a separate mode — it is this same config with the
 * end stop at `endAlpha: 0` and `endColor === color`, i.e. the base color dissolving
 * into its own fully-transparent self. Nothing in this plugin special-cases it.
 */
export interface FillGradientConfig {
  /** Start stop color, hex e.g. `#cccccc`. */
  color: string;
  /** Start stop opacity, 0..1. */
  alpha: number;
  /** End stop color, hex. */
  endColor: string;
  /** End stop opacity, 0..1. `0` (with `endColor === color`) is a vignette. */
  endAlpha: number;
  /** Side the gradient runs toward. */
  direction: FillGradientDirection;
  /** Where the transition to the end stop begins, 0..1 along the gradient (0.5 = from the middle). */
  fadeStart: number;
}

/**
 * The pre-gradient shape of the config, as written by the former `vignettePlugin` to
 * `data.vignette`. Templates saved before the generalization still carry it; it is
 * read (and upgraded in place on the next write) but never produced.
 */
interface LegacyVignetteConfig {
  color: string;
  direction: FillGradientDirection;
  fadeStart: number;
}

export interface FillGradientFacade {
  /**
   * The object's gradient config, or `undefined` when it has none (a solid fill).
   * Transparently upgrades a legacy `data.vignette` to the two-stop shape, so callers
   * never see the old form. Absence is the deliberate "no gradient" tri-state.
   */
  get(target: fabric.Object): FillGradientConfig | undefined;
  /**
   * Apply (or, with `undefined`, remove) a gradient fill on a fillable shape. With a
   * config: replaces `fill` with the derived gradient and writes `data.fillGradient`.
   * With `undefined`: restores a solid `fill` from the stored start color and clears
   * the config. Either way any legacy `data.vignette` is dropped. Records exactly one
   * history step and repaints.
   */
  set(target: fabric.Object, config: FillGradientConfig | undefined): void;
}

declare module '../core/fabric-editor' {
  interface FabricEditor {
    fillGradient?: FillGradientFacade;
  }
}

interface GradientCoords {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Gradient line per direction, in `percentage` units (0..1 fractions of the
 * object's bounding box, so the gradient scales with the shape on resize). The
 * line runs from the start stop (offset 0) to the end stop (offset 1).
 */
const COORDS_BY_DIRECTION: Record<FillGradientDirection, GradientCoords> = {
  right: { x1: 0, y1: 0, x2: 1, y2: 0 },
  left: { x1: 1, y1: 0, x2: 0, y2: 0 },
  down: { x1: 0, y1: 0, x2: 0, y2: 1 },
  up: { x1: 0, y1: 1, x2: 0, y2: 0 },
};

const DEFAULT_COLOR = '#cccccc';

const clamp01 = (v: number): number => Math.min(Math.max(v, 0), 1);

/** `fabric.Color` parses the hex so the stop can carry an independent alpha. */
const toRgba = (color: string, alpha: number): string => {
  const [r, g, b] = new fabric.Color(color).getSource();
  return `rgba(${r}, ${g}, ${b}, ${clamp01(alpha)})`;
};

/** Legacy vignette → the two-stop config it is exactly equivalent to. */
const fromLegacy = (v: LegacyVignetteConfig): FillGradientConfig => ({
  color: v.color,
  alpha: 1,
  endColor: v.color,
  endAlpha: 0,
  direction: v.direction,
  fadeStart: v.fadeStart,
});

interface GradientData {
  fillGradient?: FillGradientConfig;
  vignette?: LegacyVignetteConfig;
}

export function readFillGradient(target: fabric.Object): FillGradientConfig | undefined {
  const data = target.data as GradientData | undefined;
  if (data?.fillGradient) return data.fillGradient;
  if (data?.vignette) return fromLegacy(data.vignette);
  return undefined;
}

/**
 * Drops the legacy key by replacing `data` rather than mutating it — history
 * snapshots may hold the same object reference, and this runs with no `object:modified`
 * emit so the caller's lone `setData` stays a single undo step.
 */
function stripLegacy(target: fabric.Object): void {
  const data = target.data as Record<string, unknown> | undefined;
  if (!data || !('vignette' in data)) return;
  const next = { ...data };
  delete next.vignette;
  target.data = next;
}

/**
 * Builds the linear gradient that materializes a config: the start stop held solid
 * from offset 0 through `fadeStart`, then a smooth transition to the end stop at
 * offset 1. Each stop carries its own alpha, so a fade-to-transparent (vignette) and
 * a two-color gradient come out of this one function with no branching.
 */
export function buildFillGradient(config: FillGradientConfig): fabric.Gradient {
  const start = toRgba(config.color, config.alpha);
  const end = toRgba(config.endColor, config.endAlpha);
  return new fabric.Gradient({
    type: 'linear',
    gradientUnits: 'percentage',
    coords: COORDS_BY_DIRECTION[config.direction],
    colorStops: [
      { offset: 0, color: start },
      { offset: clamp01(config.fadeStart), color: start },
      { offset: 1, color: end },
    ],
  });
}

/**
 * Adds a two-stop gradient fill to fillable shapes (rect, circle, triangle, polygon —
 * any `fabric.Object` whose `fill` is painted).
 *
 * Unlike `rectCornersPlugin`, this plugin patches nothing — a gradient `fill` is
 * native to Fabric, so it renders and serializes with no prototype override. The
 * plugin is only a typed façade (`editor.fillGradient`) that builds the gradient on
 * the consumer's behalf (UI may not import `fabric` values) and keeps the visible
 * gradient and the persisted `data.fillGradient` config in lockstep.
 *
 * Façade `set` writes the new `fill` directly, then makes exactly one
 * `editor.objects.setData` call (the single `object:modified` emit) so the history
 * plugin records both the fill and the config change as one undo step.
 */
export function fillGradientPlugin(): EditorPlugin {
  return {
    name: FabricPluginName.FILL_GRADIENT,
    install(editor: FabricEditor) {
      editor.fillGradient = {
        get: readFillGradient,
        set(target: fabric.Object, config: FillGradientConfig | undefined): void {
          if (config) {
            // Set the derived gradient directly (no emit) so the lone setData
            // below produces a single history step capturing fill + data.
            target.set('fill', buildFillGradient(config));
            target.dirty = true;
            stripLegacy(target);
            editor.objects.setData(target, 'fillGradient', config);
          } else {
            // Restore a solid fill from the stored start color (read before the
            // config is cleared), falling back to the current string fill.
            const stored = readFillGradient(target);
            const base = stored?.color ?? (typeof target.fill === 'string' ? target.fill : DEFAULT_COLOR);
            target.set('fill', base);
            target.dirty = true;
            stripLegacy(target);
            editor.objects.setData(target, 'fillGradient', undefined);
          }
        },
      };
    },
    dispose(editor: FabricEditor) {
      editor.fillGradient = undefined;
    },
  };
}
